import {AI_PROMO_STATUSES} from '../../configs/aiMatch';
import {checkIfAiPromoJoinable, checkIfAiPromoWatchable} from './checkIfAiPromoJoinable';
import {
  TEXT_AI_MATCH_GATHERING_PREFIX,
  TEXT_AI_MATCH_START_IN_MINUTES,
  TEXT_AI_MATCH_START_IN_PREFIX,
  TEXT_AI_MATCH_START_IN_SECONDS,
  TEXT_AI_MATCH_WAITING_FIRST_PLAYER,
  TEXT_AI_MATCH_WATCH,
} from '../../constants/text/aiMatch';

// Что отсчитывает строка старта матча с ИИ
export const AI_PROMO_COUNTDOWN_KINDS = {
  // До планового старта (21:00): «Сбор игроков 5 мин 0 сек» — до самой 21:00
  GATHERING: 'gathering',
  // Сервер взвел отсчет: «Старт через 7 мин 5 сек» — до его срока
  START: 'start',
};

// Статусы, у которых есть отсчет: лобби ждет игроков, идет отсчет, или срок
// уже вышел и сервер запускает фазу банов (тогда отсчет держится на нуле)
const COUNTDOWN_STATUSES = new Set([
  AI_PROMO_STATUSES.WAITING,
  AI_PROMO_STATUSES.COUNTDOWN,
  AI_PROMO_STATUSES.STARTING,
]);

const checkIfTimestamp = (value) => typeof value === 'number' && Number.isFinite(value);

/**
 * Секунды до старта: 425 → «7 мин 5 сек», без ведущих нулей; минуты в часы не
 * переходят (окно дев-прогона бывает и дальше часа — «75 мин 0 сек»).
 * @param {number} totalSeconds
 * @returns {string}
 */
export const formatAiPromoCountdown = (totalSeconds) => {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  return `${Math.floor(seconds / 60)} ${TEXT_AI_MATCH_START_IN_MINUTES} ${seconds % 60} ${TEXT_AI_MATCH_START_IN_SECONDS}`;
};

/**
 * Сколько осталось до момента targetAt по часам сервера now: «7 мин 5 сек».
 * Неполная секунда считается целой, срок прошел — «0 мин 0 сек», без
 * отрицательного времени.
 *
 * @param {number} targetAt
 * @param {number} now - время сервера (Date.now() + поправка часов)
 * @returns {string}
 */
export const formatAiPromoTimeUntilStart = (targetAt, now) => (
  formatAiPromoCountdown(Math.ceil((targetAt - now) / 1000))
);

/**
 * Отсчет до старта матча с ИИ — общий для кнопки главного меню, промо-модалки
 * и плашки в самом лобби:
 *  - до планового старта (opensAt — 21:00 по Москве) — «Сбор игроков»: время
 *    до самой 21:00, будто матч начнется ровно тогда (раньше 21:00 сервер
 *    отсчет не взводит, а сколько он продлится, решит состав в 21:00);
 *  - после 21:00, когда сервер взвел отсчет (в лобби минимум один живой
 *    игрок), — «Старт через»: до его срока (startAt; пока сервер его не
 *    записал — projectedStartAt). Срок вышел, а баны еще не начались — на нуле;
 *  - после 21:00 без живых игроков отсчета нет — null («Ждем первого игрока»).
 * Матч начался, день отменен, лобби нет — тоже null.
 *
 * В промежутке между 21:00 и ответом сервера с взведенным отсчетом статус
 * еще waiting — тогда «Старт через» до прогноза сервера (projectedStartAt):
 * при живых игроках это тот же срок, что сервер взведет в 21:00.
 *
 * @param {Object|null} aiPromo - результат normalizeAiPromo или aiPromo
 *   дескриптора лобби
 * @param {Object} params
 * @param {number} params.now - время сервера (Date.now() + поправка часов)
 * @returns {null|{kind: string, targetAt: number, prefix: string, timeText: string}}
 */
export const getAiPromoCountdown = (aiPromo, {now}) => {
  if (!aiPromo || !COUNTDOWN_STATUSES.has(aiPromo.status) || !checkIfTimestamp(now)) {
    return null;
  }
  const {status, opensAt, startAt, projectedStartAt, liveCount} = aiPromo;
  let kind = AI_PROMO_COUNTDOWN_KINDS.START;
  let targetAt = null;
  if (status !== AI_PROMO_STATUSES.WAITING) {
    targetAt = startAt ?? projectedStartAt;
  } else if (checkIfTimestamp(opensAt) && now < opensAt) {
    kind = AI_PROMO_COUNTDOWN_KINDS.GATHERING;
    targetAt = opensAt;
  } else if (liveCount > 0) {
    targetAt = projectedStartAt ?? startAt;
  }
  if (!checkIfTimestamp(targetAt)) {
    return null;
  }
  return {
    kind,
    targetAt,
    prefix: kind === AI_PROMO_COUNTDOWN_KINDS.GATHERING ? TEXT_AI_MATCH_GATHERING_PREFIX : TEXT_AI_MATCH_START_IN_PREFIX,
    timeText: formatAiPromoTimeUntilStart(targetAt, now),
  };
};

/**
 * Вторая строка кнопки «Битва с Claude Opus» в главном меню:
 *  - матч еще не начался — отсчет getAiPromoCountdown («Сбор игроков 5 мин
 *    0 сек» до 21:00, «Старт через 7 мин 5 сек» после) или «Ждем первого
 *    игрока», если отсчета нет;
 *  - баны, выбор фракций или идущий матч — «Смотреть матч» (вход зрителем);
 *  - лобби нет, день отменен или матч сыгран — null: кнопку не показываем.
 *
 * @param {Object|null} aiPromo - результат normalizeAiPromo
 * @param {Object} [options]
 * @param {number} [options.now] - время сервера (Date.now() + поправка часов)
 * @returns {string|null}
 */
export const getAiPromoStartLabel = (aiPromo, {now = Date.now()} = {}) => {
  if (checkIfAiPromoWatchable(aiPromo)) return TEXT_AI_MATCH_WATCH;
  if (!checkIfAiPromoJoinable(aiPromo)) return null;
  const countdown = getAiPromoCountdown(aiPromo, {now});
  return countdown ? `${countdown.prefix} ${countdown.timeText}` : TEXT_AI_MATCH_WAITING_FIRST_PLAYER;
};
